import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getGravityUserId, useAuthenticatedFetch } from './useAuth';
import {
  AccessPolicyInvalidationInbox,
  applyAccessPolicyInvalidation,
  type AccessPolicyInvalidationDto,
} from './accessPolicyInvalidationCache';
import {
  INITIAL_POLL_BACKOFF,
  nextPollBackoff,
  type PollBackoffDecision,
  type PollOutcome,
  type PollBackoffState,
} from './accessPolicyInvalidationPolling';

interface InvalidationResponse {
  readonly events: readonly AccessPolicyInvalidationDto[];
  readonly cursor: { readonly createdAt: string; readonly id: string };
  readonly hasMore: boolean;
}

type InvalidationBroadcast = {
  readonly kind: 'batch';
  readonly events: readonly AccessPolicyInvalidationDto[];
  readonly cursor: InvalidationResponse['cursor'];
} | {
  readonly kind: 'catchup';
  readonly reason: 'focus' | 'visible' | 'online';
};

export const ACCESS_POLICY_POLL_INTERVAL_MS = 60_000;
export const ACCESS_POLICY_AUTH_RETRY_MS = 5 * 60_000;
const ACCESS_POLICY_LEADER_RETRY_MS = 15_000;
const ACCESS_POLICY_LEASE_MS = 75_000;

/**
 * Cross-session policy bridge. One browser-profile leader polls the durable
 * outbox; already audience-filtered value-free batches are broadcast to
 * follower tabs. Server authorization remains authoritative if this hint is
 * late or missed.
 */
function useAccessPolicyInvalidations(enabled = true): void {
  const fetcher = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  useEffect(() => {
    const principalId = getGravityUserId();
    if (!enabled || !principalId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let flushPromise: Promise<void> | null = null;
    let releaseLeader: (() => void) | null = null;
    let wakeLeader: (() => void) | null = null;
    const inbox = new AccessPolicyInvalidationInbox();
    const ownerId = crypto.randomUUID();
    const leaderKey = `creately-access-policy-invalidations.v2:${principalId}`;
    const leaseKey = `${leaderKey}:lease`;
    let cursor = {
      createdAt: new Date().toISOString(),
      id: '00000000-0000-0000-0000-000000000000',
    };
    const channel = typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel(leaderKey);

    const flush = (): Promise<void> => {
      if (flushPromise) return flushPromise;
      flushPromise = (async () => {
        while (!stopped && inbox.hasPending()) {
          const events = inbox.drain();
          for (let index = 0; index < events.length; index += 1) {
            try {
              await applyAccessPolicyInvalidation(queryClient, events[index]!);
            } catch (error) {
              inbox.retry(events.slice(index));
              throw error;
            }
          }
        }
      })().finally(() => {
        flushPromise = null;
      });
      return flushPromise;
    };
    const enqueue = (
      events: readonly AccessPolicyInvalidationDto[],
      broadcast: boolean,
    ): void => {
      const accepted = inbox.enqueue(events);
      if (broadcast && accepted.length > 0) {
        channel?.postMessage({ kind: 'batch', events: accepted, cursor } satisfies InvalidationBroadcast);
      }
      if (accepted.length > 0) void flush().catch(() => undefined);
    };

    if (channel) {
      channel.onmessage = (message: MessageEvent<InvalidationBroadcast | AccessPolicyInvalidationDto>) => {
        if (stopped) return;
        if ('kind' in message.data) {
          if (message.data.kind === 'batch') {
            cursor = laterCursor(cursor, message.data.cursor);
            enqueue(message.data.events, false);
          } else {
            wakeLeader?.();
          }
        } else if ('folderId' in message.data) {
          // One-release compatibility with v1 tabs during a rolling deploy.
          enqueue([message.data], false);
        }
      };
    }

    let backoff: PollBackoffState = INITIAL_POLL_BACKOFF;

    const poll = async (): Promise<number> => {
      let decision: PollBackoffDecision;
      let outcome: PollOutcome = 'request-failed';
      let nextCursor: InvalidationResponse['cursor'] | null = null;
      let appliedEvents: readonly AccessPolicyInvalidationDto[] = [];
      try {
        const params = new URLSearchParams({
          after: cursor.createdAt,
          afterId: cursor.id,
          limit: '100',
        });
        const response = await fetcher(`/api/access-policy-invalidations?${params}`,
          { cache: 'no-store' });
        if (response.ok) {
          const body = await response.json() as InvalidationResponse;
          nextCursor = laterCursor(cursor, body.cursor);
          enqueue(body.events, false);
          try {
            await flush();
            // The feed is acknowledged only after every visible event has
            // purged/refetched successfully. A transient cache failure is
            // replayed from the previous durable cursor.
            outcome = body.hasMore ? 'applied-with-backlog' : 'applied';
            decision = nextPollBackoff(backoff, outcome);
            appliedEvents = body.events;
          } catch {
            outcome = 'apply-failed';
            decision = nextPollBackoff(backoff, outcome);
          }
        } else if (response.status === 401 || response.status === 403) {
          outcome = 'unauthorized';
          decision = nextPollBackoff(backoff, outcome);
        } else {
          decision = nextPollBackoff(backoff, outcome);
        }
      } catch {
        decision = nextPollBackoff(backoff, outcome);
      }
      if (nextCursor && decision.acknowledgeCursor) cursor = laterCursor(cursor, nextCursor);
      backoff = decision;
      if (appliedEvents.length > 0) {
        channel?.postMessage({
          kind: 'batch',
          events: appliedEvents,
          cursor,
        } satisfies InvalidationBroadcast);
      }
      if (outcome === 'applied') return ACCESS_POLICY_POLL_INTERVAL_MS;
      if (outcome === 'unauthorized') return ACCESS_POLICY_AUTH_RETRY_MS;
      return decision.delayMs;
    };

    const runAsLeader = async (renewLease: boolean): Promise<void> => {
      await new Promise<void>((resolve) => {
        let polling = false;
        let catchupRequested = false;
        releaseLeader = resolve;
        const schedule = async (): Promise<void> => {
          if (stopped) return resolve();
          if (polling) {
            catchupRequested = true;
            return;
          }
          if (renewLease && !renewAccessPolicyLease(leaseKey, ownerId, Date.now())) {
            return resolve();
          }
          polling = true;
          const delay = await poll();
          polling = false;
          const nextDelay = catchupRequested ? 0 : delay;
          catchupRequested = false;
          if (!stopped) timer = setTimeout(() => { void schedule(); }, nextDelay);
        };
        wakeLeader = () => {
          if (stopped) return;
          if (polling) {
            catchupRequested = true;
            return;
          }
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => { void schedule(); }, 0);
        };
        void schedule();
      });
      wakeLeader = null;
      releaseLeader = null;
    };

    const tryLeadership = (): void => {
      if (stopped) return;
      if (navigator.locks) {
        void navigator.locks.request(leaderKey, { ifAvailable: true }, async (lock) => {
          if (!lock) {
            if (!stopped) timer = setTimeout(tryLeadership, ACCESS_POLICY_LEADER_RETRY_MS);
            return;
          }
          await runAsLeader(false);
        });
        return;
      }
      if (renewAccessPolicyLease(leaseKey, ownerId, Date.now())) {
        void runAsLeader(true).finally(() => {
          releaseAccessPolicyLease(leaseKey, ownerId);
          if (!stopped) timer = setTimeout(tryLeadership, ACCESS_POLICY_LEADER_RETRY_MS);
        });
      } else {
        timer = setTimeout(tryLeadership, ACCESS_POLICY_LEADER_RETRY_MS);
      }
    };

    const requestCatchup = (reason: 'focus' | 'visible' | 'online'): void => {
      if (stopped) return;
      if (wakeLeader) {
        wakeLeader();
      } else {
        channel?.postMessage({ kind: 'catchup', reason } satisfies InvalidationBroadcast);
      }
    };
    const onFocus = (): void => requestCatchup('focus');
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') requestCatchup('visible');
    };
    const onOnline = (): void => requestCatchup('online');

    tryLeadership();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      releaseLeader?.();
      releaseAccessPolicyLease(leaseKey, ownerId);
      channel?.close();
    };
  }, [enabled, fetcher, queryClient]);
}

export function laterCursor(
  left: InvalidationResponse['cursor'],
  right: InvalidationResponse['cursor'],
): InvalidationResponse['cursor'] {
  return right.createdAt > left.createdAt
    || right.createdAt === left.createdAt && right.id > left.id
    ? right
    : left;
}

export function renewAccessPolicyLease(
  key: string,
  ownerId: string,
  now: number,
  storage: Storage = localStorage,
): boolean {
  try {
    const current = JSON.parse(storage.getItem(key) ?? 'null') as {
      ownerId?: string;
      expiresAt?: number;
    } | null;
    if (current?.ownerId !== ownerId && (current?.expiresAt ?? 0) > now) return false;
    // localStorage has no compare-and-swap primitive, so two tabs can briefly
    // confirm themselves during the same acquisition race. This fallback only
    // bounds duplicate polling; navigator.locks remains the exclusive path
    // where supported, and every server read is independently authorized.
    storage.setItem(key, JSON.stringify({ ownerId, expiresAt: now + ACCESS_POLICY_LEASE_MS }));
    const confirmed = JSON.parse(storage.getItem(key) ?? 'null') as { ownerId?: string } | null;
    return confirmed?.ownerId === ownerId;
  } catch {
    return false;
  }
}

export function releaseAccessPolicyLease(
  key: string,
  ownerId: string,
  storage: Storage = localStorage,
): void {
  try {
    const current = JSON.parse(storage.getItem(key) ?? 'null') as { ownerId?: string } | null;
    if (current?.ownerId === ownerId) storage.removeItem(key);
  } catch {
    // Storage may be unavailable in private/sandboxed contexts. The bounded
    // lease expires and another tab can recover without weakening auth.
  }
}

export function AccessPolicyInvalidationBridge({ enabled = true }: { enabled?: boolean }) {
  useAccessPolicyInvalidations(enabled);
  return null;
}
